/**
 * Dialer List Service - Frontend API
 */

import api from './api';

export interface DialerList {
    _id: string;
    name: string;
    description?: string;
    campaignId?: string;
    status: 'active' | 'inactive' | 'completed';
    sourceType: 'csv' | 'manual' | 'api' | 'crm';
    importInfo?: {
        fileName: string;
        importedBy?: string;
        importedAt: string;
        totalRows: number;
        successRows: number;
        failedRows: number;
    };
    fieldMapping?: Array<{
        csvColumn: string;
        systemField: string;
        defaultValue?: string;
    }>;
    statistics: {
        totalContacts: number;
        pendingContacts: number;
        completedContacts: number;
        failedContacts: number;
        dncContacts: number;
    };
    createdAt: string;
    updatedAt?: string;
}

export interface DialerContact {
    _id: string;
    listId: string;
    campaignId?: string;
    phoneNumber: string;
    alternatePhones?: string[];
    firstName?: string;
    lastName?: string;
    customFields?: Record<string, string>;
    status: 'pending' | 'scheduled' | 'in_progress' | 'completed' | 'failed' | 'dnc' | 'skipped' | 'max_attempts';
    priority?: number;
    scheduledCallTime?: string;
    attempts?: Array<{
        attemptNumber: number;
        phoneNumberUsed: string;
        status: string;
        startTime: string;
        endTime?: string;
        duration?: number;
        notes?: string;
    }>;
    disposition?: {
        code: string;
        notes?: string;
        callbackRequested?: boolean;
        callbackTime?: string;
    };
    createdAt: string;
}

export interface CsvPreview {
    fileName: string;
    totalRows: number;
    headers: string[];
    preview: Record<string, string>[];
    suggestedMapping: Array<{
        csvColumn: string;
        systemField: string;
    }>;
}

export interface ImportResult {
    total: number;
    success: number;
    failed: number;
    errors: Array<{ row: number; error: string }>;
}

const dialerListService = {
    // List CRUD
    async getLists(filter?: { campaignId?: string; status?: string }): Promise<DialerList[]> {
        const params = new URLSearchParams();
        if (filter?.campaignId) params.append('campaignId', filter.campaignId);
        if (filter?.status) params.append('status', filter.status);
        
        const response = await api.get(`/dialer-lists?${params.toString()}`);
        return response.data.data;
    },

    async getList(id: string): Promise<{ list: DialerList; contactStats: Record<string, number> }> {
        const response = await api.get(`/dialer-lists/${id}`);
        return response.data.data;
    },

    async createList(data: { name: string; description?: string; campaignId?: string }): Promise<DialerList> {
        const response = await api.post('/dialer-lists', data);
        return response.data.data;
    },

    async updateList(id: string, data: { name?: string; description?: string; status?: string }): Promise<DialerList> {
        const response = await api.put(`/dialer-lists/${id}`, data);
        return response.data.data;
    },

    async deleteList(id: string): Promise<void> {
        await api.delete(`/dialer-lists/${id}`);
    },

    // CSV Import
    async previewCsv(listId: string, file: File): Promise<CsvPreview> {
        const formData = new FormData();
        formData.append('file', file);
        
        const response = await api.post(`/dialer-lists/${listId}/preview`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        });
        return response.data.data;
    },

    async importCsv(listId: string, file: File, fieldMapping?: Array<{ csvColumn: string; systemField: string }>): Promise<ImportResult> {
        const formData = new FormData();
        formData.append('file', file);
        if (fieldMapping) {
            formData.append('fieldMapping', JSON.stringify(fieldMapping));
        }
        
        const response = await api.post(`/dialer-lists/${listId}/import`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
            timeout: 120000 // 2 minutes for large files
        });
        return response.data.data;
    },

    // Contacts
    async getContacts(listId: string, filter?: { status?: string; page?: number; limit?: number }): Promise<{
        data: DialerContact[];
        pagination: { page: number; limit: number; total: number; pages: number };
    }> {
        const params = new URLSearchParams();
        if (filter?.status) params.append('status', filter.status);
        if (filter?.page) params.append('page', filter.page.toString());
        if (filter?.limit) params.append('limit', filter.limit.toString());
        
        const response = await api.get(`/dialer-lists/${listId}/contacts?${params.toString()}`);
        return {
            data: response.data.data,
            pagination: response.data.pagination
        };
    },

    async addContact(listId: string, contact: {
        phoneNumber: string;
        firstName?: string;
        lastName?: string;
        alternatePhones?: string[];
        customFields?: Record<string, string>;
    }): Promise<DialerContact> {
        const response = await api.post(`/dialer-lists/${listId}/contacts`, contact);
        return response.data.data;
    },

    async updateContact(contactId: string, data: Partial<DialerContact>): Promise<DialerContact> {
        const response = await api.put(`/dialer-lists/contacts/${contactId}`, data);
        return response.data.data;
    },

    async deleteContact(contactId: string): Promise<void> {
        await api.delete(`/dialer-lists/contacts/${contactId}`);
    },

    async markContactDnc(contactId: string, reason?: string): Promise<void> {
        await api.post(`/dialer-lists/contacts/${contactId}/dnc`, { reason });
    },

    async resetList(listId: string, excludeDnc: boolean = true): Promise<{ modifiedCount: number }> {
        const response = await api.post(`/dialer-lists/${listId}/reset`, { excludeDnc });
        return { modifiedCount: parseInt(response.data.message) };
    },

    // Listeyi kampanyaya ata (contact'ları kopyalar)
    async assignToCampaign(listId: string, campaignId: string): Promise<{ newList: DialerList; contactsCopied: number }> {
        const response = await api.post(`/dialer-lists/${listId}/assign`, { campaignId });
        return response.data.data;
    },

    // Kampanyasız (havuz) listeleri getir
    async getPoolLists(): Promise<DialerList[]> {
        const response = await api.get('/dialer-lists?pool=true');
        return response.data.data;
    },

    // Liste raporunu Excel olarak indir
    async exportList(listId: string): Promise<Blob> {
        const response = await api.get(`/dialer-lists/${listId}/export`, {
            responseType: 'blob',
            timeout: 120000
        });
        return response.data;
    }
};

export default dialerListService;
